import * as React from 'react';
import { GetInfluencerQuery } from 'generated/graphql';
import { ReactComponent as GoogleLogoSvg } from '../../../auth/view/assets/google-logo.svg';
import { ReactComponent as AppleLogoSvg } from '../../../auth/view/assets/apple-logo.svg';
import { useConnectedAccounts } from './useConnectedAccounts.hook';
import { AccountRow } from './AccountRow';
import { ApplePrivateRelayModal } from './ApplePrivateRelayModal';
import { ApplePrivateRelayBanner } from './ApplePrivateRelayBanner';
import { DisconnectConfirmationModal } from './DisconnectConfirmationModal';
import { useUserStore } from '@client/new/stores/user.store';
import * as styles from './ConnectedAccountsSection.css';
import { SocialProvider } from './account.types';

type Props = {
  socialAccounts: NonNullable<GetInfluencerQuery['creator']>['socialAccounts'];
  onRefresh: () => Promise<unknown> | void;
};

const providerConfigs: ReadonlyArray<{
  provider: SocialProvider;
  label: string;
  Logo: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  logoClassName?: string;
}> = [
  {
    provider: 'google',
    label: 'Google',
    Logo: GoogleLogoSvg,
    logoClassName: undefined,
  },
  {
    provider: 'apple',
    label: 'Apple',
    Logo: AppleLogoSvg,
    logoClassName: styles.appleLogo,
  },
];

const isApplePrivateRelayEmail = (email: string) =>
  email.endsWith('@privaterelay.appleid.com');

export const ConnectedAccountsSection = ({
  socialAccounts,
  onRefresh,
}: Props) => {
  const { user } = useUserStore();
  const hasPrivateRelayEmail = isApplePrivateRelayEmail(user?.email ?? '');
  const [appleRelayModalVariant, setAppleRelayModalVariant] = React.useState<
    'add-email' | 'disconnect' | null
  >(null);
  const [confirmDisconnectProvider, setConfirmDisconnectProvider] =
    React.useState<SocialProvider | null>(null);

  const {
    connectedByProvider,
    pendingProvider,
    handleConnect,
    handleDisconnect,
  } = useConnectedAccounts({ socialAccounts, onRefresh });

  const handleAppleDisconnect = () => {
    if (hasPrivateRelayEmail) {
      setAppleRelayModalVariant('disconnect');
      return;
    }
    setConfirmDisconnectProvider('apple');
  };

  const handleConfirmDisconnect = () => {
    if (!confirmDisconnectProvider) return;
    const provider = confirmDisconnectProvider;
    setConfirmDisconnectProvider(null);
    handleDisconnect(provider);
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.title}>Connected accounts</h2>
      <div className={styles.list}>
        {providerConfigs.map(({ provider, label, Logo, logoClassName }) => (
          <AccountRow
            key={provider}
            label={label}
            Logo={Logo}
            logoClassName={logoClassName}
            connected={connectedByProvider[provider]}
            isLoading={pendingProvider === provider}
            onConnect={() => handleConnect(provider)}
            onDisconnect={
              provider === 'apple'
                ? handleAppleDisconnect
                : () => setConfirmDisconnectProvider(provider)
            }
          />
        ))}
      </div>
      {hasPrivateRelayEmail && (
        <ApplePrivateRelayBanner
          onAddEmail={() => setAppleRelayModalVariant('add-email')}
        />
      )}
      <ApplePrivateRelayModal
        isOpen={appleRelayModalVariant !== null}
        variant={appleRelayModalVariant ?? 'add-email'}
        onClose={() => setAppleRelayModalVariant(null)}
        onEmailUpdated={() => handleDisconnect('apple')}
      />
      <DisconnectConfirmationModal
        isOpen={confirmDisconnectProvider !== null}
        email={user?.email ?? ''}
        onConfirm={handleConfirmDisconnect}
        onCancel={() => setConfirmDisconnectProvider(null)}
      />
    </section>
  );
};
